import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Moon, Calendar, RefreshCw} from 'lucide-react';
import { loadEphemerisData, findClosestEphemerisEntry } from '../../astro/ephemerisUtils';
import { calculateBirthChartWithLibrary } from '../../astro/horoscopeCalculator';
import useCityToCoordinates from './useCityToCoordinates';
import ChartDrawer from './ChartDrawer';

const majorCities = [
  { name: "New York, NY, USA", lat: 40.7128, lon: -74.0060 },
  { name: "Los Angeles, CA, USA", lat: 34.0522, lon: -118.2437 },
  { name: "Chicago, IL, USA", lat: 41.8781, lon: -87.6298 },
  { name: "London, UK", lat: 51.5074, lon: -0.1278 },
  { name: "Paris, France", lat: 48.8566, lon: 2.3522 },
  { name: "Berlin, Germany", lat: 52.52, lon: 13.405 },
  { name: "Amsterdam, Netherlands", lat: 52.3676, lon: 4.9041 },
  { name: "Tokyo, Japan", lat: 35.6762, lon: 139.6503 },
  { name: "Sydney, Australia", lat: -33.8688, lon: 151.2093 },
  { name: "Toronto, Canada", lat: 43.6532, lon: -79.3832 },
  { name: "Mexico City, Mexico", lat: 19.4326, lon: -99.1332 },
  { name: "Buenos Aires, Argentina", lat: -34.6118, lon: -58.396 },
  { name: "Cape Town, South Africa", lat: -33.9249, lon: 18.4241 },
  { name: "Moscow, Russia", lat: 55.7558, lon: 37.6176 },
  { name: "Beijing, China", lat: 39.9042, lon: 116.4074 },
  { name: "Delhi, India", lat: 28.7041, lon: 77.1025 },
  { name: "Bangkok, Thailand", lat: 13.7563, lon: 100.5018 },
  { name: "Rio de Janeiro, Brazil", lat: -22.9068, lon: -43.1729 }
];

const LunarReturnExperience = () => {
  const [page, setPage] = useState(0); // 0 = form, 1 = result
  const [formData, setFormData] = useState({
    birthDate: '',
    birthTime: '',
    birthPlace: '',
    latitude: '',
    longitude: '',
    lunarReturnYear: new Date().getFullYear().toString(),
    lunarReturnMonth: (new Date().getMonth() + 1).toString().padStart(2, '0')
  });
  const [isCustomPlace, setIsCustomPlace] = useState(false);
  const [customPlace, setCustomPlace] = useState('');
  const [showCoordinates, setShowCoordinates] = useState(false);
  const [lunarReturnData, setLunarReturnData] = useState(null);
  const [loading, setLoading] = useState(false);
  const { fetchCoordinates, loading: geocodingLoading, error: geocodingError } = useCityToCoordinates();

  const handleChange = ({ target: { name, value } }) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePlaceChange = async (e) => {
    const selectedValue = e.target.value;
    if (selectedValue === 'custom') {
      setIsCustomPlace(true);
      setShowCoordinates(false);
      setFormData(prev => ({ ...prev, birthPlace: '', latitude: '', longitude: '' }));
    } else if (selectedValue === '') {
      setIsCustomPlace(false);
      setShowCoordinates(false);
      setFormData(prev => ({ ...prev, birthPlace: '', latitude: '', longitude: '' }));
    } else {
      setIsCustomPlace(false);
      const selectedCity = majorCities.find(city => city.name === selectedValue);
      if (selectedCity) {
        setFormData(prev => ({
          ...prev,
          birthPlace: selectedCity.name,
          latitude: selectedCity.lat.toString(),
          longitude: selectedCity.lon.toString()
        }));
        setShowCoordinates(true);
      }
    }
  };

  const handleCustomPlaceSubmit = async () => {
    if (!customPlace.trim()) return;
    try {
      const coords = await fetchCoordinates(customPlace);
      if (coords) {
        setFormData(prev => ({
          ...prev,
          birthPlace: customPlace,
          latitude: coords.lat.toString(),
          longitude: coords.lon.toString()
        }));
        setShowCoordinates(true);
        setIsCustomPlace(false);
        setCustomPlace('');
      }
    } catch (error) {}
  };

  // Find the next lunar return date after birth
  const findNextLunarReturn = async (birthDate, birthMoonLongitude) => {
    const lunarReturnYear = parseInt(formData.lunarReturnYear);
    const lunarReturnMonth = parseInt(formData.lunarReturnMonth);
    
    // Start from the specified year and month
    let year = lunarReturnYear;
    let month = lunarReturnMonth - 1; // 0-indexed
    
    const maxMonths = 12; // Search up to 12 months
    for (let i = 0; i < maxMonths; i++) {
      const ephemerisData = await loadEphemerisData(year);
      if (!ephemerisData) {
        year++;
        month = 0;
        continue;
      }
      
      // Search through each day of the month
      for (const entry of ephemerisData) {
        if (!entry.planets || !entry.planets.Moon) continue;
        
        const entryDate = new Date(entry.date);
        if (entryDate.getFullYear() === year && entryDate.getMonth() === month) {
          const moonLon = entry.planets.Moon.longitude;
          // If Moon is within 1 degree of natal position, consider it a return
          if (Math.abs(((moonLon - birthMoonLongitude + 180) % 360) - 180) < 1) {
            return { date: entry.date, longitude: moonLon };
          }
        }
      }
      
      month++;
      if (month >= 12) {
        year++;
        month = 0;
      }
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const latitude = parseFloat(formData.latitude) || 0;
      const longitude = parseFloat(formData.longitude) || 0;
      const { birthDate, birthTime, birthPlace, lunarReturnYear, lunarReturnMonth } = formData;
      
      if (!birthDate || !birthTime || !birthPlace) {
        throw new Error('Please enter all required fields.');
      }

      // Get natal Moon longitude
      const birthDateObj = new Date(`${birthDate}T${birthTime}`);
      const birthYear = birthDateObj.getFullYear();
      const natalEphemeris = await loadEphemerisData(birthYear);
      const natalEntry = findClosestEphemerisEntry(natalEphemeris, birthDateObj);
      
      if (!natalEntry || !natalEntry.planets || !natalEntry.planets.Moon) {
        throw new Error('No natal Moon data found');
      }
      
      const natalMoonLongitude = natalEntry.planets.Moon.longitude;

      // Find lunar return date
      const lunarReturn = await findNextLunarReturn(birthDateObj, natalMoonLongitude);
      if (!lunarReturn) {
        throw new Error('No lunar return found for the specified period');
      }


      // Use the same birth chart calculation logic but with lunar return date
      const lunarReturnDate = new Date(lunarReturn.date);
      lunarReturnDate.setHours(birthDateObj.getHours());
      lunarReturnDate.setMinutes(birthDateObj.getMinutes());
      lunarReturnDate.setSeconds(birthDateObj.getSeconds());

      const chartData = await calculateBirthChartWithLibrary(lunarReturnDate, latitude, longitude, true);
      
      // Add lunar return specific information
      const lunarReturnChart = {
        ...chartData,
        lunarReturnDate: lunarReturnDate.toISOString(),
        lunarReturnYear: parseInt(lunarReturnYear),
        lunarReturnMonth: parseInt(lunarReturnMonth),
        birthDate: formData.birthDate,
        birthTime: formData.birthTime,
        birthPlace: formData.birthPlace
      };

      setLunarReturnData(lunarReturnChart);
      setPage(1);
    } catch (error) {
      console.error('Error calculating lunar return chart:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="full-screen-image-container full-height margin-top-0"
      style={{
        backgroundImage: `url('/img/saturnwallpaper.jpg')`,
      }}
    >
      {page === 0 && (
        <div className="ascendant-display-container">
          <div className="ascendant-header">
            <div className="ascendant-title">
              <Moon size={20} />
              <h1 style={{ fontSize: '1.5rem', margin: 0 }}>Lunar Return Chart</h1>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="ascendant-form">
            <div className="form-section" style={{
              background: 'rgba(0, 0, 0, 0.4)',
              borderRadius: '16px',
              padding: '1.5rem',
              marginBottom: '1.5rem',
              border: '1px solid rgba(255, 215, 0, 0.2)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
            }}>
              <div className="section-title" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginBottom: '1rem',
                color: '#FFD700',
                fontSize: '1rem',
                fontWeight: '600'
              }}>
                <Calendar size={16} />
                <span>Birth Information</span>
              </div>
              
              <div className="form-row">
                <div className="form-field">
                  <label>Birth Date</label>
                  <input
                    type="date"
                    name="birthDate"
                    value={formData.birthDate}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-field">
                  <label>Birth Time</label>
                  <input
                    type="time"
                    name="birthTime"
                    value={formData.birthTime}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
              
              <div className="form-field">
                <label>Birth Place</label>
                <select value={isCustomPlace ? 'custom' : formData.birthPlace} onChange={handlePlaceChange} required>
                  <option value="">Select a city...</option>
                  {majorCities.map(city => (
                    <option key={city.name} value={city.name}>{city.name}</option>
                  ))}
                  <option value="custom">Custom...</option>
                </select>
                {isCustomPlace && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <input type="text" value={customPlace} onChange={e => setCustomPlace(e.target.value)} placeholder="Enter custom place" />
                    <button type="button" onClick={handleCustomPlaceSubmit} disabled={geocodingLoading} style={{ marginLeft: 8 }}>
                      {geocodingLoading ? 'Locating...' : 'Set Location'}
                    </button>
                    {geocodingError && <div style={{ color: 'red', marginTop: 4 }}>{geocodingError}</div>}
                  </div>
                )}
              </div>

              <div className="form-row">
                <div className="form-field">
                  <label>Lunar Return Year</label>
                  <input
                    type="number"
                    name="lunarReturnYear"
                    value={formData.lunarReturnYear}
                    onChange={handleChange}
                    min={1900}
                    max={2100}
                    required
                  />
                </div>
                <div className="form-field">
                  <label>Lunar Return Month</label>
                  <select name="lunarReturnMonth" value={formData.lunarReturnMonth} onChange={handleChange} required>
                    <option value="01">January</option>
                    <option value="02">February</option>
                    <option value="03">March</option>
                    <option value="04">April</option>
                    <option value="05">May</option>
                    <option value="06">June</option>
                    <option value="07">July</option>
                    <option value="08">August</option>
                    <option value="09">September</option>
                    <option value="10">October</option>
                    <option value="11">November</option>
                    <option value="12">December</option>
                  </select>
                </div>
              </div>
            </div>
            
            <div className="form-actions">
              <button
                type="submit"
                className="calculate-button"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <RefreshCw size={18} className="spinning" />
                    Calculating...
                  </>
                ) : (
                  <>
                    <Moon size={18} />
                    Calculate Lunar Return
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}
      
      {page === 1 && lunarReturnData && (
        <motion.div
          key="result"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 50 }}
          transition={{ duration: 0.4 }}
          className="has-text-centered"
        >
          <div className="card modern-card" style={{ maxWidth: 600, margin: "1.5rem auto", padding: "1.5rem", boxShadow: "0 4px 32px rgba(0,0,0,0.12)" }}>
            <div style={{ marginBottom: "1rem" }}>
              <h2 style={{ color: "#FFD700", marginBottom: "0.5rem", fontSize: "1.3rem" }}>Lunar Return Chart {lunarReturnData.lunarReturnYear}</h2>
              <p style={{ color: "#fff", opacity: 0.8, fontSize: "0.9rem" }}>
                {new Date(lunarReturnData.lunarReturnDate).toLocaleDateString()} • {lunarReturnData.birthPlace}
              </p>
            </div>
            <ChartDrawer {...lunarReturnData} ascendantLongitude={lunarReturnData?.ascendantLongitude || 0} />
          </div>
          <motion.button
            className="button is-warning"
            style={{
              position: "fixed",
              top: 100,
              left: 24,
              zIndex: 1000,
              boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
              borderRadius: "12px",
              padding: "12px 16px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontSize: "14px",
              fontWeight: "600",
              border: "none",
              background: "linear-gradient(135deg, #ffc107 0%, #ff8f00 100%)",
              color: "#000",
              cursor: "pointer",
              transition: "all 0.3s ease"
            }}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            onClick={() => window.location.href = "/lunarreturn"}
            onMouseEnter={(e) => {
              e.target.style.transform = "translateY(-2px)";
              e.target.style.boxShadow = "0 6px 16px rgba(0,0,0,0.4)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = "0 4px 12px rgba(0,0,0,0.3)";
            }}
          >
            <ArrowLeft size={20} />
            Back
          </motion.button>
        </motion.div>
      )}
    </div>
  );
};

export default LunarReturnExperience; 
